import { useEffect } from 'react'
import React from 'react'
import { useDispatch } from 'react-redux'
import { Tab, Tabs, TabList, TabPanel, TabsProps, TabListProps, TabProps, TabPanelProps, ReactTabsFunctionComponent } from 'react-tabs';
import { addPeopleIcon, peopleIcon } from '../ui/icons';
import { FilterList } from '../ui/FilterList';
import { RenderErrors } from '../ui/ErrorComponent';
import { api, ReadAs } from '../../api';
import { SidePanel } from '../ui/SidePanel';
import { getTranslator, Translator } from '../../Translator';
import { addNewPartyAdmin, makeVoterPartyAdmin, selectPaScreenShown } from '../../features/partyadmin/PartyAdminScreenSlice';
import { voterSearched } from '../../features/search/searchSlice';
import { useAppSelector } from '../../hooks';

interface SelectPAProps {
    electionId: number
    partyId: number
}

const ElecTabs: ReactTabsFunctionComponent<TabsProps> = (props: TabsProps) =>
    <Tabs {
        ...{
            ...props,
            className: props.className ?? "react-tabs",
            disabledTabClassName: props.disabledTabClassName ?? "react-tabs__tab--disabled",
            selectedTabClassName: props.selectedTabClassName ?? "react-tabs__tab--selected",
            selectedTabPanelClassName: props.selectedTabPanelClassName ?? "react-tabs__tab-panel--selected"
        }
    } />;

ElecTabs.tabsRole = 'Tabs';

const ElecTabList: ReactTabsFunctionComponent<TabListProps> = (props: TabListProps) =>
    <TabList {
        ...{
            ...props,
            className: props.className ?? "react-tabs__tab-list",
        }
    } />;

ElecTabList.tabsRole = 'TabList';

const ElecTab: ReactTabsFunctionComponent<TabProps> = (props: TabProps) =>
    <Tab {
        ...{
            ...props,
            className: props.className ?? "react-tabs__tab",
            disabledClassName: props.disabledClassName ?? "react-tabs__tab--disabled",
            selectedClassName: props.selectedClassName ?? "react-tabs__tab--selected",
        }
    } />;

ElecTab.tabsRole = 'Tab';

const ElecTabPanel: ReactTabsFunctionComponent<TabPanelProps> = (props: TabPanelProps) =>
    <TabPanel {
        ...{
            ...props,
            className: props.className ?? "react-tabs__tab-panel",
            selectedClassName: props.selectedClassName ?? "react-tabs__tab-panel--selected",
        }
    } />;

ElecTabPanel.tabsRole = 'TabPanel';

export const SelectPartyAdminScreen = (props: SelectPAProps) => {
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);
    const language = useAppSelector(state => state.language);
    const parties = useAppSelector(state => state.parties);
    const partyAdminScreenVisible = useAppSelector(state => state.partyAdminScreen.visible)
    const dispatch = useDispatch();
    const t = getTranslator(language);

    const searchTerm = useAppSelector(state => state.searchItemState.SearchTermVoter);
    const party = parties.filter(x => x.PartyId === props.partyId)[0];

    useEffect(() => {
        fetchVoters();
    }, []); //door loze param (de array) wordt useeffect eenmalig uitgevoerd

    const [voterUsernames, setVoterUsernames] = React.useState<string[] | null>(null);

    const fetchVoters = async () => {
        return await api(token).Elections.VoterUsernamesForElection<void>(props.electionId)
            .ok(voterUsernames => setVoterUsernames(voterUsernames), ReadAs.Json);
    }

    return <SidePanel visible={partyAdminScreenVisible} toggleAction={() => dispatch(selectPaScreenShown({ visible: false }))}>
        <ElecTabs>
            <ElecTabList>
                <ElecTab>{peopleIcon}{t['Voters']}</ElecTab>
                <ElecTab>{addPeopleIcon}{t['Non-voters']}</ElecTab>
            </ElecTabList>
            <ElecTabPanel>
                <h1>{t['All Voters']}</h1>
                <FilterList<string>
                    items={voterUsernames || []}
                    searchTerm={searchTerm}
                    onChange={v => dispatch(voterSearched(v))}
                    filter={(username, searchTerm) => username.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1}
                    render={user =>
                        <div key={user}>
                            <a style={{ cursor: 'pointer' }} className="party-admin-screen__voter" onClick={() => confirm(t['confirmMakePartyAdmin'](user, party.Name)) && makeVoterPartyAdmin(user, props.partyId, token, dispatch)}>
                                👤 {user}
                            </a>
                        </div>}
                    label={t['Search voters for this election']}
                    language={t.languageCode}
                />
            </ElecTabPanel>
            <ElecTabPanel>
                <h1>{t['Add administrator to party']}</h1>
                <AddPartyAdminForm t={t} partyId={props.partyId} />
            </ElecTabPanel>
        </ElecTabs>
    </SidePanel>
}

enum FormState { Invalid, Valid, Saved }
interface AddPAProps { partyId: number, t: Translator }
export const AddPartyAdminForm = (props: AddPAProps) => {
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);
    const dispatch = useDispatch();

    const [userName, setUserName] = React.useState<string>('');
    const [errors, setErrors] = React.useState<string[]>([]);
    const [formState, setFormState] = React.useState<FormState>(FormState.Invalid);
    const t = props.t;

    const userNameInputRef: React.RefObject<HTMLInputElement|null> = React.createRef();

    useEffect(() => {
        userNameInputRef && userNameInputRef.current && userNameInputRef.current.focus();
    }, []); //door loze param (de array) wordt useeffect eenmalig uitgevoerd

    var validationTimer = 0;

    const onChange = () => {
        setFormState(FormState.Invalid);
        clearTimeout(validationTimer);
        validationTimer = window.setTimeout(validatePartyAdmin, 1000);
    }

    const validatePartyAdmin = () => {
        api(token).get<void>(`/api/partyadministrators/validate?partyId=${encodeURIComponent(props.partyId)}&userName=${encodeURIComponent(userName)}`)
            .ok(() => { setErrors([]); setFormState(FormState.Valid); })
            .anyStatus(errors => setErrors(errors || []), ReadAs.ValidationErrors);
    }

    const submit = () => {
        return addNewPartyAdmin(userName, props.partyId, token, dispatch).then(result => {
            if (result.success) {
                setFormState(FormState.Saved);
                setErrors([]);
            } else {
                let errors = result.errors;
                if (!errors.length) {
                    errors.push(t['An error occurred during saving.']);
                }
                setFormState(FormState.Invalid);
                setErrors(result.errors);
            }
        })
    }

    return <form className="editor" onSubmit={e => { submit(); e.preventDefault(); }} onChange={onChange}>
        <label>
            {t['User name']}
            <input type="text" onChange={e => setUserName(e.target.value)} ref={userNameInputRef} />
        </label>
        <button type="submit" disabled={formState !== FormState.Valid}>{t['Add']}</button>
        {formState === FormState.Saved && <div className="statusbanner--ok" style={{ backgroundColor: 'white', color: 'black' }}>{t['Your changes were successfully saved.']}</div>}
        <div style={{ color: 'red' }}><RenderErrors errors={errors} /></div>
    </form>
}
